import React,{useState,useEffect,useCallback,useMemo,useRef} from 'react';
import {posAPI,branchAPI} from '../utils/api';
import {PageHeader,Badge,Modal,fmt,fmtDate} from '../components/common';
import {useLang} from '../i18n';
import {
  Monitor,Clock,DollarSign,ShoppingCart,TrendingUp,
  ChevronDown,ChevronUp,ChevronRight,X,RefreshCw,Download,
  Search,Calendar,BarChart2,Banknote,
  ArrowUpCircle,User,
  CheckCircle,Circle,Receipt,
  Building2,TimerReset,Filter,
  TrendingDown,Users,Percent,Award
} from 'lucide-react';
import toast from 'react-hot-toast';
import {useAuth} from '../context/AuthContext';
const NoPerm=({action})=>(<div style={{display:'flex',alignItems:'center',gap:8,padding:'12px 16px',background:'var(--dm)',borderRadius:10,fontSize:13,color:'var(--danger)',marginBottom:16}}><span style={{fontSize:18}}>🔒</span> You don't have permission to {action}.</div>);
import {
  BarChart,Bar,XAxis,YAxis,CartesianGrid,Tooltip,ResponsiveContainer,
  PieChart as RPieChart,Pie,Cell,Legend,AreaChart,Area,LineChart,Line
} from 'recharts';

/* ── helpers ── */
const dur=(a,b)=>{
  const ms=(b?new Date(b):new Date())-new Date(a);
  const h=Math.floor(ms/3600000);
  const m=Math.floor((ms%3600000)/60000);
  return h>0?`${h}h ${m}m`:`${m}m`;
};

const fmtTime=d=>d?new Date(d).toLocaleTimeString('en-US',{hour:'2-digit',minute:'2-digit'}):'—';
const fmtDateTime=d=>d?new Date(d).toLocaleString('en-US',{month:'short',day:'numeric',hour:'2-digit',minute:'2-digit'}):'—';
const fmtDateOnly=d=>d?new Date(d).toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}):'—';
const METHOD_COLORS={'Cash':'#22c55e','Card':'#6366f1','ABA':'#3b82f6','Wing':'#f59e0b','Pi Pay':'#ec4899','KHQR':'#8b5cf6','Other':'#94a3b8'};
const getMethodColor=n=>METHOD_COLORS[n]||`hsl(${(n||'').charCodeAt(0)*37%360},60%,55%)`;
const CHART_COLORS=['#6366f1','#22c55e','#f59e0b','#ef4444','#8b5cf6','#3b82f6','#ec4899','#14b8a6'];

/* ── Print styles ── */
function injectPrintStyle(){
  if(document.getElementById('pos-report-ps'))return;
  const s=document.createElement('style');s.id='pos-report-ps';
  s.textContent=`@media print{body *{visibility:hidden}#pos-report-print,#pos-report-print *{visibility:visible}#pos-report-print{position:fixed;inset:0;width:100%;padding:24px;background:#fff;font-family:system-ui,sans-serif;color:#111}@page{margin:12mm;size:A4 landscape}}`;
  document.head.appendChild(s);
}

/* ── Skeleton loader ── */
function Skeleton({w='100%',h=16,r=6,mb=8}){
  return<div style={{width:w,height:h,borderRadius:r,background:'var(--border)',marginBottom:mb,animation:'pulse 1.5s ease-in-out infinite',opacity:.6}}/>;
}
function ReportSkeleton(){
  return(
    <div>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:18}}>
        {[1,2,3,4].map(i=><div key={i} style={{background:'var(--s2)',borderRadius:10,padding:'14px'}}><Skeleton h={10} w={70}/><Skeleton h={22} w={90} mb={4}/><Skeleton h={10} w={50} mb={0}/></div>)}
      </div>
      {[1,2,3].map(i=>(
        <div key={i} style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px 18px',marginBottom:10}}>
          <div style={{display:'flex',alignItems:'center',gap:14}}>
            <Skeleton w={32} h={32} r={16} mb={0}/>
            <div style={{flex:1}}><Skeleton h={16} w="40%" mb={6}/><Skeleton h={10} w="25%" mb={0}/></div>
            <Skeleton w={80} h={24} mb={0}/>
          </div>
          <Skeleton h={5} r={3} mb={0} style={{marginTop:12}}/>
        </div>
      ))}
    </div>
  );
}
const STATUS_CFG={
  open:   {label:'Open',   variant:'success', glow:'rgba(34,197,94,.25)'},
  closing:{label:'Closing',variant:'warning', glow:'rgba(245,158,11,.25)'},
  closed: {label:'Closed', variant:'default', glow:'none'},
};
const today=()=>new Date().toISOString().split('T')[0];
const monthStart=()=>{const d=new Date();d.setDate(1);return d.toISOString().split('T')[0]};

/* ── Reusable stat card ── */
function Stat({icon,label,value,sub,color='var(--primary)',bg,small,onClick}){
  return(
    <div onClick={onClick} style={{background:bg||'var(--s2)',borderRadius:10,padding:small?'10px 12px':'12px 14px',
      display:'flex',alignItems:'center',gap:10,cursor:onClick?'pointer':'default',
      transition:'background .15s'}}>
      <div style={{width:small?30:36,height:small?30:36,borderRadius:9,background:color+'22',
        display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0,color}}>
        {icon}
      </div>
      <div style={{minWidth:0}}>
        <div style={{fontSize:small?9:10,color:'var(--td)',fontWeight:600,letterSpacing:.3,textTransform:'uppercase'}}>{label}</div>
        <div style={{fontSize:small?14:16,fontWeight:800,color:'var(--text)',lineHeight:1.2}}>{value}</div>
        {sub&&<div style={{fontSize:10,color:'var(--td)',marginTop:1}}>{sub}</div>}
      </div>
    </div>
  );
}

/* ── Payment method pill ── */
function MethodPill({name,amount,count,originalAmount,originalCurrency}){
  const c=getMethodColor(name);
  const isKHR=originalCurrency==='KHR';
  return(
    <div style={{display:'flex',alignItems:'center',gap:5,padding:'4px 9px',borderRadius:16,
      background:c+'18',border:`1px solid ${c}44`,flexShrink:0}}>
      <div style={{width:6,height:6,borderRadius:'50%',background:c,flexShrink:0}}/>
      <span style={{fontSize:11,fontWeight:700,color:c}}>{name}</span>
      {isKHR&&originalAmount?(
        <span style={{fontSize:11,fontWeight:800,color:'var(--text)'}}>
          ៛{Math.round(originalAmount).toLocaleString()}
          <span style={{fontSize:10,color:'var(--td)',marginLeft:3}}>≈{fmt(amount)}</span>
        </span>
      ):(
        <span style={{fontSize:12,fontWeight:800,color:'var(--text)'}}>{fmt(amount)}</span>
      )}
      {count>0&&<span style={{fontSize:10,color:'var(--td)'}}>×{count}</span>}
    </div>
  );
}

/* ═══════════════════════════════════════════
   BRANCH REPORT TAB
═══════════════════════════════════════════ */
function BranchReportTab({branches}){
  const{isOwner,myBranchId}=useAuth();
  const[dateFrom,setDateFrom]=useState(monthStart());
  const[dateTo,setDateTo]=useState(today());
  const[branchFilter,setBranchFilter]=useState('');
  const[loading,setLoading]=useState(false);
  const[report,setReport]=useState(null);
  const[overall,setOverall]=useState(null);
  const[selectedBranch,setSelectedBranch]=useState(()=>myBranchId&&!isOwner?myBranchId:null); // branch detail drill-down
  const[drillTab,setDrillTab]=useState('overview'); // overview|products|cashiers|hourly|daily
  const {t} = useLang();
  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const p={date_from:dateFrom,date_to:dateTo};
      if(!isOwner&&myBranchId) p.branch_id=myBranchId;  // non-owners only see their branch
      else if(branchFilter) p.branch_id=branchFilter;
      const r=await posAPI.getBranchReport(p);
      const reportData=r.data.report||[];
      setReport(reportData);
      setOverall(r.data.overall||{});
      // Auto-select: non-owners default to their branch; owners default to first result
      if(!isOwner&&myBranchId){
        setSelectedBranch(myBranchId);
      } else if(reportData.length===1){
        setSelectedBranch(reportData[0].id);
      } else {
        setSelectedBranch(null);
      }
    }catch(e){toast.error(e.response?.data?.error||'Failed to load report')}
    finally{setLoading(false)}
  },[dateFrom,dateTo,branchFilter,isOwner,myBranchId]);

  useEffect(()=>{load()},[load]);

  /* CSV export */
  const exportCSV=()=>{
    if(!report?.length)return;
    const rows=[['Branch','Code','Transactions','Total Sales','Avg Transaction','Discount','Unique Customers','Top Payment Method'].join(',')];
    report.forEach(b=>{
      rows.push([
        `"${b.name}"`,b.code,b.transaction_count,b.total_sales.toFixed(2),
        b.avg_transaction.toFixed(2),b.total_discount.toFixed(2),
        b.unique_customers,`"${b.payment_methods[0]?.name||'—'}"`
      ].join(','));
    });
    // Product detail sheet
    rows.push('');
    rows.push(['Branch','Product','SKU','Qty Sold','Revenue','Transactions'].join(','));
    report.forEach(b=>{
      b.top_products.forEach(p=>{
        rows.push([`"${b.name}"`,`"${p.name}"`,p.sku,p.qty.toFixed(0),p.revenue.toFixed(2),p.transactions].join(','));
      });
    });
    const blob=new Blob([rows.join('\n')],{type:'text/csv'});
    const a=document.createElement('a');
    a.href=URL.createObjectURL(blob);
    a.download=`pos-branch-report-${dateFrom}-to-${dateTo}.csv`;
    a.click();
    toast.success('Report exported');
  };

  /* Date shortcuts */
  const setRange=(from,to)=>{setDateFrom(from);setDateTo(to)};
  const shortcuts=[
    {label:t('posSessions.today'),fn:()=>setRange(today(),today())},
    {label:t('posSessions.thisWeek'),fn:()=>{const d=new Date();d.setDate(d.getDate()-d.getDay());setRange(d.toISOString().split('T')[0],today())}},
    {label:t('posSessions.thisMonth'),fn:()=>setRange(monthStart(),today())},
    {label:t('posSessions.last30'),fn:()=>{const d=new Date();d.setDate(d.getDate()-29);setRange(d.toISOString().split('T')[0],today())}},
  ];

  const selectedData=report?.find(b=>b.id===selectedBranch);
  const totalSalesAll=report?.reduce((s,b)=>s+b.total_sales,0)||0;
  const printRef=useRef();

  // Inject print style once
  useEffect(()=>{injectPrintStyle()},[]);

  const handlePrint=()=>{
    if(!report?.length){toast.error('Load a report first');return;}
    window.print();
  };

  // Build multi-branch daily comparison data
  const multiDailyData=useMemo(()=>{
    if(!report?.length)return[];
    const dateSet=new Set();
    report.forEach(b=>b.daily.forEach(d=>dateSet.add(d.date)));
    const dates=[...dateSet].sort();
    return dates.map(date=>({
      date,
      ...Object.fromEntries(report.map(b=>{
        const d=b.daily.find(x=>x.date===date);
        return[b.name,d?d.sales:0];
      }))
    }));
  },[report]);

  const DrillBtn=({k,label})=>(
    <button onClick={()=>setDrillTab(k)} style={{padding:'6px 14px',borderRadius:8,border:'none',cursor:'pointer',fontSize:12,fontWeight:700,
      background:drillTab===k?'var(--primary)':'transparent',color:drillTab===k?'#fff':'var(--td)',transition:'all .15s'}}>
      {label}
    </button>
  );

  return(
    <div>
      {/* ── Filter bar ── */}
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'14px 16px',marginBottom:16}}>
        <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:5}}>FROM</div>
            <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
              style={{padding:'8px 10px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:13}}/>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:5}}>TO</div>
            <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
              style={{padding:'8px 10px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:13}}/>
          </div>
          {isOwner&&<div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:5}}>BRANCH</div>
            <select value={branchFilter} onChange={e=>setBranchFilter(e.target.value)}
              style={{padding:'8px 10px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:13,minWidth:160}}>
              <option value="">All Branches</option>
              {branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>}
          {!isOwner&&myBranchId&&<div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:5}}>BRANCH</div>
            <div style={{display:'flex',alignItems:'center',gap:6,padding:'8px 12px',borderRadius:8,border:'1.5px solid var(--primary)',background:'var(--pm)',color:'var(--primary)',fontSize:13,fontWeight:700,height:38,boxSizing:'border-box'}}>
              🔒 {branches.find(b=>Number(b.id)===Number(myBranchId))?.name||'Your Branch'}
            </div>
          </div>}
          <button className="btn btn-primary" onClick={load} disabled={loading} style={{gap:6,height:38,alignSelf:'flex-end'}}>
            {loading?<RefreshCw size={13} style={{animation:'spin 1s linear infinite'}}/>:<Filter size={13}/>}
            {loading?t('common.loading'):t('posSessions.runReport')}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={exportCSV} disabled={!report?.length}
            style={{height:38,gap:5,alignSelf:'flex-end'}}>
            <Download size={13}/> {t('common.export')}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={handlePrint} disabled={!report?.length}
            style={{height:38,gap:5,alignSelf:'flex-end'}}>
            <Receipt size={13}/> Print
          </button>
        </div>
        {/* Shortcuts */}
        <div style={{display:'flex',gap:5,marginTop:10,flexWrap:'wrap'}}>
          {shortcuts.map(s=>(
            <button key={s.label} onClick={s.fn} className="btn btn-ghost btn-sm" style={{fontSize:11,padding:'3px 10px'}}>{s.label}</button>
          ))}
        </div>
      </div>

      {/* ── Overall KPIs ── */}
      {overall&&report&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:10,marginBottom:18}}>
          <Stat icon={<DollarSign size={15}/>} label={t('posSessions.totalPosSales')} value={fmt(overall.total_sales)} color="var(--primary)"/>
          <Stat icon={<ShoppingCart size={15}/>} label={t('common.transactions')} value={overall.transaction_count} color="var(--success)"/>
          <Stat icon={<Building2 size={15}/>} label="Active Branches" value={overall.branch_count} color="var(--info)"/>
          <Stat icon={<TrendingUp size={15}/>} label="Avg / Transaction"
            value={fmt(overall.transaction_count?overall.total_sales/overall.transaction_count:0)} color="var(--accent)"/>
        </div>
      )}

      {/* ── Loading skeleton ── */}
      {loading&&<ReportSkeleton/>}

      {/* ── No data ── */}
      {!loading&&report?.length===0&&(
        <div style={{textAlign:'center',padding:'60px 20px',color:'var(--td)'}}>
          <Receipt size={48} style={{opacity:.15,display:'block',margin:'0 auto 16px'}}/>
          <div style={{fontSize:16,fontWeight:600,marginBottom:6}}>No POS data found</div>
          <div style={{fontSize:13}}>No completed sales transactions in the selected date range.</div>
        </div>
      )}

      {/* ── Branch comparison cards ── */}
      {report&&report.length>0&&!selectedBranch&&(
        <div>
          {/* Rank header */}
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
            <div style={{fontSize:14,fontWeight:800,display:'flex',alignItems:'center',gap:8}}>
              <Award size={16} color="var(--primary)"/>Branch Performance Ranking
            </div>
            <div style={{fontSize:12,color:'var(--td)'}}>{fmtDate(dateFrom)} → {fmtDate(dateTo)}</div>
          </div>

          {/* Branch cards */}
          <div style={{display:'flex',flexDirection:'column',gap:10,marginBottom:24}}>
            {report.map((b,rank)=>{
              const pct=totalSalesAll>0?(b.total_sales/totalSalesAll*100):0;
              const rankEmoji=rank===0?'🥇':rank===1?'🥈':rank===2?'🥉':`#${rank+1}`;
              const topMethod=b.payment_methods[0];
              return(
                <div key={b.id} onClick={()=>{setSelectedBranch(b.id);setDrillTab('overview')}}
                  style={{background:'var(--surface)',border:'1.5px solid var(--border)',borderRadius:14,
                    padding:'16px 18px',cursor:'pointer',transition:'all .15s',position:'relative',overflow:'hidden'}}
                  onMouseEnter={e=>{e.currentTarget.style.borderColor='var(--primary)';e.currentTarget.style.boxShadow='0 4px 20px rgba(79,140,255,.1)'}}
                  onMouseLeave={e=>{e.currentTarget.style.borderColor='var(--border)';e.currentTarget.style.boxShadow='none'}}>

                  {/* Progress bar accent at top */}
                  <div style={{position:'absolute',top:0,left:0,height:3,width:`${pct}%`,
                    background:`linear-gradient(90deg,${CHART_COLORS[rank%CHART_COLORS.length]},${CHART_COLORS[(rank+1)%CHART_COLORS.length]})`,
                    borderRadius:'14px 0 0 0',transition:'width .6s'}}/>

                  <div style={{display:'flex',alignItems:'center',gap:14,flexWrap:'wrap'}}>
                    {/* Rank + name */}
                    <div style={{display:'flex',alignItems:'center',gap:10,flex:'1 1 200px',minWidth:0}}>
                      <div style={{fontSize:22,lineHeight:1,flexShrink:0}}>{rankEmoji}</div>
                      <div style={{minWidth:0}}>
                        <div style={{fontSize:16,fontWeight:800,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{b.name}</div>
                        <div style={{fontSize:11,color:'var(--td)',marginTop:2,display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
                          {b.code&&<span style={{fontFamily:'monospace',background:'var(--s2)',padding:'1px 6px',borderRadius:4}}>{b.code}</span>}
                          <span>{b.transaction_count} txns</span>
                          <span>{b.unique_customers} customers</span>
                        </div>
                      </div>
                    </div>

                    {/* Key metrics */}
                    <div style={{display:'flex',gap:16,flexWrap:'wrap',alignItems:'center'}}>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>REVENUE</div>
                        <div style={{fontSize:22,fontWeight:900,color:'var(--primary)'}}>{fmt(b.total_sales)}</div>
                        <div style={{fontSize:10,color:'var(--td)'}}>{pct.toFixed(1)}% of total</div>
                      </div>
                      <div style={{textAlign:'right'}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>AVG TXN</div>
                        <div style={{fontSize:16,fontWeight:800}}>{fmt(b.avg_transaction)}</div>
                      </div>
                      {b.total_discount>0&&<div style={{textAlign:'right'}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>DISCOUNT</div>
                        <div style={{fontSize:14,fontWeight:700,color:'var(--warning)'}}>-{fmt(b.total_discount)}</div>
                      </div>}
                    </div>

                    {/* Payment methods */}
                    <div style={{display:'flex',gap:5,flexWrap:'wrap',alignItems:'center',flex:'0 0 auto'}}>
                      {b.payment_methods.slice(0,3).map(m=>(
                        <MethodPill key={m.name} name={m.name} amount={m.total} count={m.count}/>
                      ))}
                      {b.payment_methods.length>3&&<span style={{fontSize:10,color:'var(--td)'}}>+{b.payment_methods.length-3}</span>}
                    </div>

                    <div style={{display:'flex',alignItems:'center',gap:5,flexShrink:0,color:'var(--td)'}}>
                      <span style={{fontSize:12}}>View details</span>
                      <ChevronRight size={14}/>
                    </div>
                  </div>

                  {/* Share bar */}
                  <div style={{marginTop:12,height:5,background:'var(--border)',borderRadius:5,overflow:'hidden'}}>
                    <div style={{height:'100%',width:`${pct}%`,
                      background:CHART_COLORS[rank%CHART_COLORS.length],
                      borderRadius:5,transition:'width .6s'}}/>
                  </div>
                </div>
              );
            })}
          </div>

          {/* Cross-branch comparison chart */}
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:24}}>
            {/* Revenue bar chart */}
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px'}}>
              <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Revenue by Branch</div>
              <div style={{height:200}}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={report.map(b=>({name:b.code||b.name.slice(0,8),sales:b.total_sales,txns:b.transaction_count}))}
                    margin={{top:4,right:8,bottom:4,left:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                    <XAxis dataKey="name" tick={{fontSize:11,fill:'var(--td)'}} tickLine={false}/>
                    <YAxis tickFormatter={v=>fmt(v)} tick={{fontSize:10,fill:'var(--td)'}} width={55}/>
                    <Tooltip formatter={(v,n)=>n==='sales'?fmt(v):v}
                      contentStyle={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:12}}/>
                    <Bar dataKey="sales" name="Revenue" radius={[5,5,0,0]}>
                      {report.map((_,i)=><Cell key={i} fill={CHART_COLORS[i%CHART_COLORS.length]}/>)}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </div>

            {/* Payment methods aggregate pie */}
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px'}}>
              <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Payment Methods (All Branches)</div>
              {(()=>{
                const allMethods={};
                report.forEach(b=>b.payment_methods.forEach(m=>{
                  if(!allMethods[m.name])allMethods[m.name]={name:m.name,total:0,count:0};
                  allMethods[m.name].total+=m.total;allMethods[m.name].count+=m.count;
                }));
                const data=Object.values(allMethods).sort((a,b)=>b.total-a.total);
                if(!data.length)return<div style={{textAlign:'center',padding:40,color:'var(--td)',fontSize:12}}>No data</div>;
                return(
                  <div style={{height:200}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <RPieChart>
                        <Pie data={data} dataKey="total" nameKey="name" cx="50%" cy="50%" innerRadius={45} outerRadius={80} paddingAngle={3}>
                          {data.map((m,i)=><Cell key={i} fill={getMethodColor(m.name)}/>)}
                        </Pie>
                        <Tooltip formatter={v=>fmt(v)} contentStyle={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:12}}/>
                        <Legend iconSize={9} iconType="circle" wrapperStyle={{fontSize:11}}/>
                      </RPieChart>
                    </ResponsiveContainer>
                  </div>
                );
              })()}
            </div>
          </div>

          {/* Multi-branch daily comparison line chart */}
          {multiDailyData.length>1&&report.length>1&&(
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px',marginBottom:24}}>
              <div style={{fontSize:13,fontWeight:800,marginBottom:4}}>Daily Sales Comparison — All Branches</div>
              <div style={{fontSize:11,color:'var(--td)',marginBottom:12}}>Revenue per day overlaid for each branch</div>
              <div style={{height:240}}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={multiDailyData} margin={{top:4,right:16,bottom:24,left:0}}>
                    <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                    <XAxis dataKey="date" tick={{fontSize:9,fill:'var(--td)'}} tickLine={false} angle={-30} textAnchor="end"/>
                    <YAxis tickFormatter={v=>fmt(v)} tick={{fontSize:9,fill:'var(--td)'}} width={55}/>
                    <Tooltip formatter={(v,n)=>[fmt(v),n]}
                      contentStyle={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:11}}/>
                    <Legend iconSize={9} iconType="circle" wrapperStyle={{fontSize:11}}/>
                    {report.map((b,i)=>(
                      <Line key={b.id} type="monotone" dataKey={b.name}
                        stroke={CHART_COLORS[i%CHART_COLORS.length]} strokeWidth={2}
                        dot={false} activeDot={{r:4}}/>
                    ))}
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Top cashiers across all branches */}
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px',marginBottom:24}}>
            <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Top Cashiers (All Branches)</div>
            {(()=>{
              const cashierMap={};
              report.forEach(b=>b.cashiers.forEach(c=>{
                if(!cashierMap[c.id])cashierMap[c.id]={...c,branch:b.name,branchCode:b.code};
                else{cashierMap[c.id].sales+=c.sales;cashierMap[c.id].count+=c.count;}
              }));
              const list=Object.values(cashierMap).sort((a,b)=>b.sales-a.sales).slice(0,8);
              if(!list.length)return<div style={{textAlign:'center',padding:20,color:'var(--td)',fontSize:12}}>No cashier data</div>;
              return(
                <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(220px,1fr))',gap:10}}>
                  {list.map((c,i)=>(
                    <div key={c.id} style={{display:'flex',alignItems:'center',gap:10,padding:'10px 12px',background:'var(--s2)',borderRadius:10}}>
                      <div style={{width:34,height:34,borderRadius:'50%',
                        background:CHART_COLORS[i%CHART_COLORS.length]+'33',
                        color:CHART_COLORS[i%CHART_COLORS.length],
                        display:'flex',alignItems:'center',justifyContent:'center',fontSize:13,fontWeight:900,flexShrink:0}}>
                        {i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}
                      </div>
                      <div style={{flex:1,minWidth:0}}>
                        <div style={{fontSize:13,fontWeight:700,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{c.name}</div>
                        <div style={{fontSize:10,color:'var(--td)',marginTop:1}}>{c.branch} · {c.count} txns</div>
                      </div>
                      <div style={{textAlign:'right',flexShrink:0}}>
                        <div style={{fontSize:14,fontWeight:900,color:'var(--primary)'}}>{fmt(c.sales)}</div>
                        <div style={{fontSize:10,color:'var(--td)'}}>{fmt(c.count?c.sales/c.count:0)}/txn</div>
                      </div>
                    </div>
                  ))}
                </div>
              );
            })()}
          </div>

          {/* Top products across all branches */}
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px',marginBottom:24}}>
            <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Top Products (All Branches)</div>
            {(()=>{
              const prodMap={};
              report.forEach(b=>b.top_products.forEach(p=>{
                if(!prodMap[p.id])prodMap[p.id]={...p};
                else{prodMap[p.id].qty+=p.qty;prodMap[p.id].revenue+=p.revenue;prodMap[p.id].transactions+=p.transactions;}
              }));
              const list=Object.values(prodMap).sort((a,b)=>b.revenue-a.revenue).slice(0,10);
              if(!list.length)return<div style={{textAlign:'center',padding:20,color:'var(--td)',fontSize:12}}>No product data</div>;
              const maxRev=list[0]?.revenue||1;
              return(
                <div style={{display:'flex',flexDirection:'column',gap:7}}>
                  {list.map((p,i)=>{
                    const pct=(p.revenue/maxRev*100);
                    return(
                      <div key={p.id} style={{display:'flex',alignItems:'center',gap:12,padding:'9px 12px',background:'var(--s2)',borderRadius:9}}>
                        <div style={{width:22,height:22,borderRadius:6,background:CHART_COLORS[i%CHART_COLORS.length]+'33',
                          color:CHART_COLORS[i%CHART_COLORS.length],display:'flex',alignItems:'center',justifyContent:'center',
                          fontSize:10,fontWeight:900,flexShrink:0}}>{i+1}</div>
                        <div style={{flex:1,minWidth:0}}>
                          <div style={{fontSize:13,fontWeight:600,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{p.name}</div>
                          <div style={{marginTop:4,height:4,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
                            <div style={{height:'100%',width:`${pct}%`,background:CHART_COLORS[i%CHART_COLORS.length],borderRadius:3,transition:'width .4s'}}/>
                          </div>
                        </div>
                        <div style={{display:'flex',gap:16,flexShrink:0,textAlign:'right'}}>
                          <div>
                            <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>QTY</div>
                            <div style={{fontSize:13,fontWeight:700}}>{p.qty.toFixed(0)}</div>
                          </div>
                          <div>
                            <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>REVENUE</div>
                            <div style={{fontSize:14,fontWeight:900,color:'var(--primary)'}}>{fmt(p.revenue)}</div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              );
            })()}
          </div>
          {/* Summary comparison table */}
          <div id="pos-report-print" style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,overflow:'hidden',marginBottom:24}}>
            <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <div style={{fontSize:13,fontWeight:800}}>Branch Summary Table</div>
              <div style={{fontSize:11,color:'var(--td)'}}>{fmtDate(dateFrom)} → {fmtDate(dateTo)}</div>
            </div>
            <div style={{overflowX:'auto'}}>
              <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                <thead>
                  <tr style={{background:'var(--s2)'}}>
                    {['Branch','Code','Revenue','Share','Txns','Avg Txn','Customers','Discount','Top Method'].map((h,i)=>(
                      <th key={h} style={{padding:'9px 14px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                        letterSpacing:.4,color:'var(--td)',textAlign:i>=2?'right':'left',
                        borderBottom:'1px solid var(--border)',whiteSpace:'nowrap'}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {report.map((b,i)=>{
                    const pct=totalSalesAll>0?(b.total_sales/totalSalesAll*100).toFixed(1):0;
                    return(
                      <tr key={b.id} style={{borderBottom:'1px solid var(--border)',cursor:'pointer'}}
                        onClick={()=>{setSelectedBranch(b.id);setDrillTab('overview')}}
                        onMouseEnter={e=>e.currentTarget.style.background='var(--s2)'}
                        onMouseLeave={e=>e.currentTarget.style.background=''}>
                        <td style={{padding:'10px 14px',fontWeight:700}}>
                          <span style={{marginRight:6}}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':''}</span>{b.name}
                        </td>
                        <td style={{padding:'10px 14px',fontFamily:'monospace',fontSize:11,color:'var(--td)'}}>{b.code||'—'}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',fontSize:15,fontWeight:900,color:'var(--primary)'}}>{fmt(b.total_sales)}</td>
                        <td style={{padding:'10px 14px',textAlign:'right'}}>
                          <div style={{display:'flex',alignItems:'center',gap:6,justifyContent:'flex-end'}}>
                            <div style={{width:48,height:5,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
                              <div style={{height:'100%',width:`${pct}%`,background:CHART_COLORS[i%CHART_COLORS.length],borderRadius:3}}/>
                            </div>
                            <span style={{fontSize:11,color:'var(--td)',minWidth:32}}>{pct}%</span>
                          </div>
                        </td>
                        <td style={{padding:'10px 14px',textAlign:'right',fontWeight:600}}>{b.transaction_count}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',color:'var(--td)'}}>{fmt(b.avg_transaction)}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',color:'var(--td)'}}>{b.unique_customers}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',color:'var(--warning)',fontWeight:600}}>
                          {b.total_discount>0?`-${fmt(b.total_discount)}`:'—'}
                        </td>
                        <td style={{padding:'10px 14px',textAlign:'right'}}>
                          {b.payment_methods[0]&&(
                            <span style={{fontSize:11,fontWeight:700,color:getMethodColor(b.payment_methods[0].name)}}>
                              {b.payment_methods[0].name}
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr style={{background:'var(--s2)',fontWeight:800}}>
                    <td colSpan={2} style={{padding:'10px 14px',fontSize:13}}>TOTAL</td>
                    <td style={{padding:'10px 14px',textAlign:'right',fontSize:15,color:'var(--primary)'}}>{fmt(totalSalesAll)}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',color:'var(--td)',fontSize:11}}>100%</td>
                    <td style={{padding:'10px 14px',textAlign:'right'}}>{report.reduce((s,b)=>s+b.transaction_count,0)}</td>
                    <td style={{padding:'10px 14px',textAlign:'right',color:'var(--td)'}}>
                      {fmt(report.reduce((s,b)=>s+b.transaction_count,0)?totalSalesAll/report.reduce((s,b)=>s+b.transaction_count,0):0)}
                    </td>
                    <td colSpan={3}/>
                  </tr>
                </tfoot>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── Branch drill-down ── */}
      {selectedBranch&&selectedData&&(
        <div>
          {/* Back + header */}
          <div style={{display:'flex',alignItems:'center',gap:12,marginBottom:16}}>
            <button onClick={()=>setSelectedBranch(null)} className="btn btn-ghost btn-sm" style={{gap:5}}>
              ← Back to All Branches
            </button>
            <div style={{flex:1}}>
              <div style={{fontSize:18,fontWeight:900}}>{selectedData.name}</div>
              <div style={{fontSize:12,color:'var(--td)',marginTop:1}}>
                {fmtDate(dateFrom)} → {fmtDate(dateTo)} · {selectedData.transaction_count} transactions
              </div>
            </div>
          </div>

          {/* Branch KPIs */}
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(150px,1fr))',gap:10,marginBottom:16}}>
            <Stat icon={<DollarSign size={14}/>} label={t('posSessions.byRevenue')} value={fmt(selectedData.total_sales)} color="var(--primary)"/>
            <Stat icon={<ShoppingCart size={14}/>} label={t('common.transactions')} value={selectedData.transaction_count} color="var(--success)"/>
            <Stat icon={<TrendingUp size={14}/>} label="Avg Transaction" value={fmt(selectedData.avg_transaction)} color="var(--accent)"/>
            <Stat icon={<Users size={14}/>} label={t('posSessions.uniqueCustomers')} value={selectedData.unique_customers} color="var(--info)"/>
            <Stat icon={<TrendingDown size={14}/>} label={t('posSessions.totalDiscount')} value={fmt(selectedData.total_discount)} color="var(--warning)"/>
            <Stat icon={<Percent size={14}/>} label="Discount Rate"
              value={selectedData.total_sales>0?`${((selectedData.total_discount/selectedData.total_sales)*100).toFixed(1)}%`:'0%'}
              color="var(--td)"/>
          </div>

          {/* Drill tabs */}
          <div style={{display:'flex',gap:3,padding:'4px',background:'var(--s2)',borderRadius:10,width:'fit-content',marginBottom:16}}>
            {[['overview','Overview'],['products','Products'],['cashiers','Cashiers'],['daily','Daily Trend'],['hourly','By Hour']].map(([k,l])=>(
              <DrillBtn key={k} k={k} label={l}/>
            ))}
          </div>

          {/* OVERVIEW */}
          {drillTab==='overview'&&(
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
              {/* Payment methods */}
              <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px'}}>
                <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Payment Breakdown</div>
                {selectedData.payment_methods.length===0
                  ?<div style={{textAlign:'center',padding:30,color:'var(--td)',fontSize:12}}>No payment data</div>
                  :<>
                    <div style={{height:180}}>
                      <ResponsiveContainer width="100%" height="100%">
                        <RPieChart>
                          <Pie data={selectedData.payment_methods} dataKey="total" nameKey="name" cx="50%" cy="50%" innerRadius={40} outerRadius={72} paddingAngle={3}>
                            {selectedData.payment_methods.map((m,i)=><Cell key={i} fill={getMethodColor(m.name)}/>)}
                          </Pie>
                          <Tooltip formatter={v=>fmt(v)} contentStyle={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:12}}/>
                        </RPieChart>
                      </ResponsiveContainer>
                    </div>
                    <div style={{display:'flex',flexDirection:'column',gap:6}}>
                      {selectedData.payment_methods.map(m=>{
                        const pct=selectedData.total_sales>0?(m.total/selectedData.total_sales*100).toFixed(1):0;
                        const c=getMethodColor(m.name);
                        return(
                          <div key={m.name} style={{display:'flex',alignItems:'center',gap:8,fontSize:12}}>
                            <div style={{width:8,height:8,borderRadius:'50%',background:c,flexShrink:0}}/>
                            <span style={{flex:1,fontWeight:600}}>{m.name}</span>
                            <span style={{color:'var(--td)'}}>{pct}% · ×{m.count}</span>
                            <span style={{fontWeight:800}}>{fmt(m.total)}</span>
                          </div>
                        );
                      })}
                    </div>
                  </>}
              </div>

              {/* Daily area chart */}
              <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px'}}>
                <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Sales Trend</div>
                {selectedData.daily.length===0
                  ?<div style={{textAlign:'center',padding:40,color:'var(--td)',fontSize:12}}>No data</div>
                  :<div style={{height:240}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={selectedData.daily} margin={{top:4,right:8,bottom:20,left:0}}>
                        <defs>
                          <linearGradient id="branchGrad" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.25}/>
                            <stop offset="95%" stopColor="var(--primary)" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                        <XAxis dataKey="date" tick={{fontSize:9,fill:'var(--td)'}} tickLine={false} angle={-30} textAnchor="end"/>
                        <YAxis tickFormatter={v=>fmt(v)} tick={{fontSize:9,fill:'var(--td)'}} width={52}/>
                        <Tooltip formatter={v=>fmt(v)} contentStyle={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:11}}/>
                        <Area type="monotone" dataKey="sales" name="Sales" stroke="var(--primary)" fill="url(#branchGrad)" strokeWidth={2}/>
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
            </div>
          )}

          {/* PRODUCTS */}
          {drillTab==='products'&&(
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,overflow:'hidden'}}>
              <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',fontSize:13,fontWeight:800}}>
                Top Products — {selectedData.name}
              </div>
              {selectedData.top_products.length===0
                ?<div style={{textAlign:'center',padding:40,color:'var(--td)',fontSize:12}}>No product data</div>
                :<table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'var(--s2)'}}>
                      {['Rank','Product','SKU','Qty Sold','Revenue','Txns','Revenue Bar'].map((h,i)=>(
                        <th key={h} style={{padding:'9px 14px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                          letterSpacing:.4,color:'var(--td)',textAlign:i>=3?'right':'left',
                          borderBottom:'1px solid var(--border)'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedData.top_products.map((p,i)=>{
                      const maxRev=selectedData.top_products[0]?.revenue||1;
                      const pct=(p.revenue/maxRev*100);
                      return(
                        <tr key={p.id} style={{borderBottom:'1px solid var(--border)'}}>
                          <td style={{padding:'10px 14px',fontSize:12,color:'var(--td)',fontWeight:700}}>
                            {i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}
                          </td>
                          <td style={{padding:'10px 14px'}}>
                            <div style={{fontSize:13,fontWeight:600}}>{p.name}</div>
                          </td>
                          <td style={{padding:'10px 14px',fontSize:11,fontFamily:'monospace',color:'var(--td)'}}>{p.sku||'—'}</td>
                          <td style={{padding:'10px 14px',textAlign:'right',fontSize:13,fontWeight:600}}>{p.qty.toFixed(0)}</td>
                          <td style={{padding:'10px 14px',textAlign:'right',fontSize:14,fontWeight:900,color:'var(--primary)'}}>{fmt(p.revenue)}</td>
                          <td style={{padding:'10px 14px',textAlign:'right',fontSize:12,color:'var(--td)'}}>{p.transactions}</td>
                          <td style={{padding:'10px 14px',width:120}}>
                            <div style={{height:6,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
                              <div style={{height:'100%',width:`${pct}%`,background:CHART_COLORS[i%CHART_COLORS.length],borderRadius:3}}/>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>}
            </div>
          )}

          {/* CASHIERS */}
          {drillTab==='cashiers'&&(
            <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,overflow:'hidden'}}>
              <div style={{padding:'12px 16px',borderBottom:'1px solid var(--border)',fontSize:13,fontWeight:800}}>
                Cashier Performance — {selectedData.name}
              </div>
              {selectedData.cashiers.length===0
                ?<div style={{textAlign:'center',padding:40,color:'var(--td)',fontSize:12}}>No cashier data</div>
                :<table style={{width:'100%',borderCollapse:'collapse'}}>
                  <thead>
                    <tr style={{background:'var(--s2)'}}>
                      {['Rank','Cashier','Transactions','Total Sales','Avg/Txn','Discount Given'].map((h,i)=>(
                        <th key={h} style={{padding:'9px 14px',fontSize:10,fontWeight:700,textTransform:'uppercase',
                          letterSpacing:.4,color:'var(--td)',textAlign:i>=2?'right':'left',
                          borderBottom:'1px solid var(--border)'}}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {selectedData.cashiers.map((c,i)=>(
                      <tr key={c.id} style={{borderBottom:'1px solid var(--border)'}}>
                        <td style={{padding:'10px 14px',fontSize:16}}>{i===0?'🥇':i===1?'🥈':i===2?'🥉':i+1}</td>
                        <td style={{padding:'10px 14px'}}>
                          <div style={{fontSize:13,fontWeight:700}}>{c.name}</div>
                        </td>
                        <td style={{padding:'10px 14px',textAlign:'right',fontSize:13,fontWeight:600}}>{c.count}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',fontSize:15,fontWeight:900,color:'var(--primary)'}}>{fmt(c.sales)}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',fontSize:13,color:'var(--td)'}}>{fmt(c.count?c.sales/c.count:0)}</td>
                        <td style={{padding:'10px 14px',textAlign:'right',fontSize:12,color:'var(--warning)'}}>
                          {c.discount>0?`-${fmt(c.discount)}`:'—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>}
            </div>
          )}

          {/* DAILY TREND */}
          {drillTab==='daily'&&(
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px'}}>
                <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Daily Sales — {selectedData.name}</div>
                {selectedData.daily.length===0
                  ?<div style={{textAlign:'center',padding:40,color:'var(--td)',fontSize:12}}>No daily data</div>
                  :<div style={{height:260}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={selectedData.daily} margin={{top:4,right:8,bottom:24,left:0}}>
                        <defs>
                          <linearGradient id="dg" x1="0" y1="0" x2="0" y2="1">
                            <stop offset="5%" stopColor="var(--primary)" stopOpacity={0.2}/>
                            <stop offset="95%" stopColor="var(--primary)" stopOpacity={0}/>
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                        <XAxis dataKey="date" tick={{fontSize:10,fill:'var(--td)'}} angle={-30} textAnchor="end" tickLine={false}/>
                        <YAxis tickFormatter={v=>fmt(v)} tick={{fontSize:9,fill:'var(--td)'}} width={55}/>
                        <Tooltip formatter={v=>fmt(v)} contentStyle={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:12}}/>
                        <Area type="monotone" dataKey="sales" name="Sales" stroke="var(--primary)" fill="url(#dg)" strokeWidth={2.5}/>
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
              {/* Daily table */}
              {selectedData.daily.length>0&&(
                <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,overflow:'hidden'}}>
                  <table style={{width:'100%',borderCollapse:'collapse'}}>
                    <thead><tr style={{background:'var(--s2)'}}>
                      {['Date','Transactions','Revenue','Avg Txn'].map((h,i)=>(
                        <th key={h} style={{padding:'9px 14px',fontSize:10,fontWeight:700,textTransform:'uppercase',letterSpacing:.4,color:'var(--td)',textAlign:i>=1?'right':'left',borderBottom:'1px solid var(--border)'}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {[...selectedData.daily].reverse().map(d=>(
                        <tr key={d.date} style={{borderBottom:'1px solid var(--border)'}}>
                          <td style={{padding:'9px 14px',fontSize:13,fontWeight:600}}>{fmtDateOnly(d.date)}</td>
                          <td style={{padding:'9px 14px',textAlign:'right',fontSize:13}}>{d.count}</td>
                          <td style={{padding:'9px 14px',textAlign:'right',fontSize:14,fontWeight:800,color:'var(--primary)'}}>{fmt(d.sales)}</td>
                          <td style={{padding:'9px 14px',textAlign:'right',fontSize:12,color:'var(--td)'}}>{fmt(d.count?d.sales/d.count:0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}

          {/* HOURLY */}
          {drillTab==='hourly'&&(
            <div style={{display:'flex',flexDirection:'column',gap:14}}>
              <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px'}}>
                <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Sales by Hour — {selectedData.name}</div>
                {selectedData.hourly.length===0
                  ?<div style={{textAlign:'center',padding:40,color:'var(--td)',fontSize:12}}>No hourly data</div>
                  :<div style={{height:240}}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={selectedData.hourly} margin={{top:4,right:8,bottom:4,left:0}}>
                        <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                        <XAxis dataKey="hour" tickFormatter={h=>`${h}:00`} tick={{fontSize:10,fill:'var(--td)'}} tickLine={false}/>
                        <YAxis tickFormatter={v=>fmt(v)} tick={{fontSize:9,fill:'var(--td)'}} width={52}/>
                        <Tooltip formatter={(v,n)=>n==='sales'?fmt(v):v} labelFormatter={h=>`${h}:00 – ${h+1}:00`}
                          contentStyle={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:12}}/>
                        <Bar dataKey="sales" name="Sales" fill="var(--primary)" radius={[4,4,0,0]}>
                          {selectedData.hourly.map((h,i)=><Cell key={i} fill={h.sales>0?'var(--primary)':'var(--border)'}/>)}
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>}
              </div>
              {/* Peak hours */}
              {selectedData.hourly.length>0&&(()=>{
                const sorted=[...selectedData.hourly].sort((a,b)=>b.sales-a.sales).slice(0,6);
                return(
                  <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,padding:'16px'}}>
                    <div style={{fontSize:13,fontWeight:800,marginBottom:12}}>Peak Hours</div>
                    <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(140px,1fr))',gap:8}}>
                      {sorted.map((h,i)=>(
                        <div key={h.hour} style={{background:'var(--s2)',borderRadius:10,padding:'12px 14px',
                          border:i===0?'1.5px solid var(--primary)':'1px solid var(--border)'}}>
                          <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>{h.hour}:00 – {h.hour+1}:00</div>
                          <div style={{fontSize:18,fontWeight:900,color:i===0?'var(--primary)':'var(--text)',marginTop:2}}>{fmt(h.sales)}</div>
                          <div style={{fontSize:11,color:'var(--td)',marginTop:1}}>{h.count} txn{h.count!==1?'s':''}</div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   SESSION DETAIL MODAL
═══════════════════════════════════════════ */
function SessionDetailModal({session,onClose}){
    const{t}=useLang();
  const[tab,setTab]=useState('overview');
  if(!session) return null;
  const sc=STATUS_CFG[session.status]||STATUS_CFG.closed;
  const txns=session.transaction_list||[];
  const sales=txns.filter(t=>t.type==='sale'&&t.status==='completed');
  const refunds=txns.filter(t=>t.type==='refund');
  const voided=txns.filter(t=>t.status==='voided');
  const methods=session.payment_methods||[];
  const hourly=session.hourly||[];
  const diff=parseFloat(session.difference)||0;

  const tabStyle=(t)=>({
    padding:'7px 14px',borderRadius:8,border:'none',cursor:'pointer',fontSize:12,fontWeight:700,
    background:tab===t?'var(--primary)':'transparent',
    color:tab===t?'#fff':'var(--td)',transition:'all .15s',
  });

  return(
    <Modal open onClose={onClose} title={
      <div style={{display:'flex',alignItems:'center',gap:10}}>
        <Monitor size={16}/>
        <span>Session #{session.id}</span>
        <Badge variant={sc.variant}>{sc.label}</Badge>
      </div>
    } width={780}>
      <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8,marginBottom:16}}>
        <Stat icon={<DollarSign size={15}/>} label={t('posSessions.totalSales')} value={fmt(session.total_sales)} color="var(--primary)"/>
        <Stat icon={<ShoppingCart size={15}/>} label={t('common.transactions')} value={session.total_transactions||0} color="var(--success)"/>
        <Stat icon={<Clock size={15}/>} label={t('posSessions.duration')} value={dur(session.opened_at,session.closed_at)} color="var(--info)"/>
        <Stat icon={<Banknote size={15}/>} label="Cash Balance" value={fmt(session.closing_balance??session.opening_balance)}
          sub={diff!==0?`Diff: ${diff>0?'+':''}${fmt(diff)}`:undefined}
          color={diff<0?'var(--danger)':diff>0?'var(--success)':'var(--td)'}/>
      </div>

      <div style={{display:'flex',gap:4,marginBottom:14,padding:'4px',background:'var(--s2)',borderRadius:10,width:'fit-content'}}>
        {[['overview','Overview'],['transactions','Transactions'],['payments','Payments'],['hourly','Hourly']].map(([k,l])=>(
          <button key={k} style={tabStyle(k)} onClick={()=>setTab(k)}>{l}</button>
        ))}
      </div>

      {tab==='overview'&&<div>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14,marginBottom:14}}>
          <div style={{background:'var(--s2)',borderRadius:12,padding:'14px 16px'}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:10}}>SESSION INFO</div>
            {[
              ['Branch',session.branch?.name],
              ['Cashier',session.cashier?`${session.cashier.first_name} ${session.cashier.last_name}`:'—'],
              ['Opened',fmtDateTime(session.opened_at)],
              ['Closed',session.closed_at?fmtDateTime(session.closed_at):'Still open'],
              ['Duration',dur(session.opened_at,session.closed_at)],
              ['Status',<Badge key="s" variant={sc.variant}>{sc.label}</Badge>],
            ].map(([k,v])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:7,fontSize:13}}>
                <span style={{color:'var(--td)',fontSize:12}}>{k}</span>
                <span style={{fontWeight:600}}>{v}</span>
              </div>
            ))}
          </div>
          <div style={{background:'var(--s2)',borderRadius:12,padding:'14px 16px'}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:10}}>CASH RECONCILIATION</div>
            {[
              ['Opening Balance',fmt(session.opening_balance),'var(--td)'],
              ['Cash Sales',fmt(methods.find(m=>m.name==='Cash')?.total||0),'var(--success)'],
              ['Expected',fmt(session.expected_balance||0),'var(--text)'],
              ['Actual Closing',fmt(session.closing_balance||0),'var(--text)'],
            ].map(([k,v,c])=>(
              <div key={k} style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:6,fontSize:12}}>
                <span style={{color:'var(--td)'}}>{k}</span>
                <span style={{fontWeight:700,color:c}}>{v}</span>
              </div>
            ))}
            <div style={{borderTop:'1px solid var(--border)',paddingTop:8,marginTop:4,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
              <span style={{fontWeight:700,fontSize:13}}>Difference</span>
              <span style={{fontWeight:900,fontSize:15,color:diff<-0.01?'var(--danger)':diff>0.01?'var(--success)':'var(--td)'}}>
                {diff>0?'+':''}{fmt(diff)}
              </span>
            </div>
          </div>
        </div>
        {methods.length>0&&<div style={{background:'var(--s2)',borderRadius:12,padding:'14px 16px',marginBottom:14}}>
          <div style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:10}}>PAYMENT METHODS</div>
          <div style={{display:'flex',gap:8,flexWrap:'wrap',marginBottom:10}}>
            {methods.map(m=><MethodPill key={m.name} name={m.name} amount={m.total} count={m.count}/>)}
          </div>
          <div style={{display:'flex',height:7,borderRadius:7,overflow:'hidden',gap:1}}>
            {methods.map(m=>{
              const pct=parseFloat(session.total_sales)>0?(m.total/parseFloat(session.total_sales))*100:0;
              return <div key={m.name} style={{flex:pct,background:getMethodColor(m.name),minWidth:pct>0?2:0}} title={`${m.name}: ${fmt(m.total)}`}/>;
            })}
          </div>
        </div>}
        <div style={{display:'grid',gridTemplateColumns:'repeat(3,1fr)',gap:8}}>
          <Stat small icon={<ShoppingCart size={13}/>} label="Sales" value={sales.length} sub={fmt(sales.reduce((s,t)=>s+t.total_amount,0))} color="var(--success)"/>
          <Stat small icon={<ArrowUpCircle size={13}/>} label={t('posSessions.refunds')} value={refunds.length} sub={refunds.length?fmt(refunds.reduce((s,t)=>s+t.total_amount,0)):'—'} color="var(--warning)"/>
          <Stat small icon={<X size={13}/>} label="Voided" value={voided.length} sub={voided.length?'Cancelled':'—'} color="var(--danger)"/>
        </div>
      </div>}

      {tab==='transactions'&&<div style={{maxHeight:420,overflowY:'auto'}}>
        {txns.length===0?<div style={{textAlign:'center',padding:40,color:'var(--td)'}}>No transactions</div>:
        <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
          <thead><tr style={{background:'var(--s2)'}}>
            {['#','Time','Customer','Type','Status','Methods','Total'].map(h=>(
              <th key={h} style={{padding:'8px 10px',textAlign:'left',fontWeight:700,color:'var(--td)',fontSize:11,whiteSpace:'nowrap'}}>{h}</th>
            ))}
          </tr></thead>
          <tbody>
            {txns.map((t,i)=>(
              <tr key={i} style={{borderBottom:'1px solid var(--border)',opacity:t.status==='voided'?.5:1}}>
                <td style={{padding:'8px 10px',fontFamily:'monospace',color:'var(--primary)',fontWeight:700,fontSize:11}}>{t.transaction_number}</td>
                <td style={{padding:'8px 10px',whiteSpace:'nowrap',color:'var(--td)'}}>{fmtTime(t.created_at)}</td>
                <td style={{padding:'8px 10px',maxWidth:120,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.customer?.name||'Walk-in'}</td>
                <td style={{padding:'8px 10px'}}><Badge variant={t.type==='sale'?'success':t.type==='refund'?'warning':'default'}>{t.type}</Badge></td>
                <td style={{padding:'8px 10px'}}><Badge variant={t.status==='completed'?'success':t.status==='voided'?'danger':'default'}>{t.status}</Badge></td>
                <td style={{padding:'8px 10px'}}>
                  <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                    {(t.payment_methods||[]).map((m,j)=>(
                      <span key={j} style={{fontSize:10,fontWeight:700,color:getMethodColor(m),background:getMethodColor(m)+'20',padding:'1px 6px',borderRadius:10}}>{m}</span>
                    ))}
                  </div>
                </td>
                <td style={{padding:'8px 10px',fontWeight:800,color:t.type==='refund'?'var(--warning)':'var(--text)',textAlign:'right'}}>
                  {t.type==='refund'?'-':''}{fmt(t.total_amount)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr style={{background:'var(--s2)',fontWeight:800}}>
              <td colSpan={6} style={{padding:'8px 10px',fontSize:12}}>Total ({sales.length} sales)</td>
              <td style={{padding:'8px 10px',fontSize:14,color:'var(--primary)',textAlign:'right'}}>{fmt(session.total_sales)}</td>
            </tr>
          </tfoot>
        </table>}
      </div>}

      {tab==='payments'&&<div>
        {methods.length===0?<div style={{textAlign:'center',padding:40,color:'var(--td)'}}>No payment data</div>:<>
          <div style={{height:220,marginBottom:16}}>
            <ResponsiveContainer width="100%" height="100%">
              <RPieChart>
                <Pie data={methods} dataKey="total" nameKey="name" cx="50%" cy="50%" innerRadius={55} outerRadius={90} paddingAngle={3} label={({name,percent})=>`${name} ${(percent*100).toFixed(0)}%`} labelLine={false}>
                  {methods.map((m,i)=><Cell key={i} fill={getMethodColor(m.name)}/>)}
                </Pie>
                <Tooltip formatter={v=>fmt(v)}/>
              </RPieChart>
            </ResponsiveContainer>
          </div>
          <div style={{display:'flex',flexDirection:'column',gap:6}}>
            {methods.sort((a,b)=>b.total-a.total).map(m=>{
              const pct=parseFloat(session.total_sales)>0?(m.total/parseFloat(session.total_sales)*100).toFixed(1):0;
              const c=getMethodColor(m.name);
              return(
                <div key={m.name} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:'var(--s2)',borderRadius:10}}>
                  <div style={{width:10,height:10,borderRadius:'50%',background:c,flexShrink:0}}/>
                  <span style={{flex:1,fontSize:13,fontWeight:700}}>{m.name}</span>
                  <span style={{fontSize:11,color:'var(--td)'}}>×{m.count} txns</span>
                  <div style={{width:100,height:6,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
                    <div style={{width:`${pct}%`,height:'100%',background:c,borderRadius:3}}/>
                  </div>
                  <span style={{fontSize:11,color:'var(--td)',minWidth:32,textAlign:'right'}}>{pct}%</span>
                  <span style={{fontSize:14,fontWeight:900,color:'var(--text)',minWidth:70,textAlign:'right'}}>{fmt(m.total)}</span>
                </div>
              );
            })}
          </div>
        </>}
      </div>}

      {tab==='hourly'&&<div>
        {hourly.length===0?<div style={{textAlign:'center',padding:40,color:'var(--td)'}}>No hourly data</div>:<>
          <div style={{height:220,marginBottom:14}}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={hourly} margin={{top:4,right:8,bottom:4,left:0}}>
                <CartesianGrid strokeDasharray="3 3" stroke="var(--border)"/>
                <XAxis dataKey="hour" tickFormatter={h=>`${h}:00`} tick={{fontSize:10,fill:'var(--td)'}}/>
                <YAxis tickFormatter={v=>fmt(v)} tick={{fontSize:10,fill:'var(--td)'}} width={55}/>
                <Tooltip formatter={(v,n)=>n==='sales'?fmt(v):v} labelFormatter={h=>`${h}:00 – ${h+1}:00`}
                  contentStyle={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:8,fontSize:12}}/>
                <Bar dataKey="sales" name="Sales" fill="var(--primary)" radius={[4,4,0,0]}/>
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(100px,1fr))',gap:6}}>
            {hourly.map(h=>(
              <div key={h.hour} style={{background:'var(--s2)',borderRadius:8,padding:'8px 10px',textAlign:'center'}}>
                <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>{h.hour}:00</div>
                <div style={{fontSize:13,fontWeight:800,color:'var(--primary)'}}>{fmt(h.sales)}</div>
                <div style={{fontSize:10,color:'var(--td)'}}>{h.count} sale{h.count!==1?'s':''}</div>
              </div>
            ))}
          </div>
        </>}
      </div>}

      <div className="modal-footer" style={{marginTop:16}}>
        <button className="btn btn-ghost" onClick={onClose}>{t('common.close')}</button>
      </div>
    </Modal>
  );
}

/* ── Session Card ── */
function SessionCard({session,onView,expanded,onToggle}){
  const sc=STATUS_CFG[session.status]||STATUS_CFG.closed;
  const cashier=session.cashier;
  const methods=session.payment_methods||[];
  const diff=parseFloat(session.difference)||0;

  return(
    <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:14,overflow:'hidden',transition:'box-shadow .15s',
      boxShadow:session.status==='open'?`0 0 0 2px var(--success),0 4px 20px ${sc.glow}`:'none'}}>
      <div style={{padding:'14px 16px',display:'flex',alignItems:'center',gap:12,flexWrap:'wrap'}}>
        <div style={{display:'flex',alignItems:'center',gap:8,flexShrink:0}}>
          <div style={{width:10,height:10,borderRadius:'50%',
            background:session.status==='open'?'var(--success)':session.status==='closing'?'var(--warning)':'var(--border)',
            boxShadow:session.status==='open'?'0 0 8px var(--success)':session.status==='closing'?'0 0 8px var(--warning)':'none'}}/>
          <span style={{fontSize:12,fontWeight:700,color:'var(--td)',fontFamily:'monospace'}}>#{session.id}</span>
          <Badge variant={sc.variant}>{sc.label}</Badge>
        </div>
        <div style={{flex:1,minWidth:0}}>
          <div style={{display:'flex',alignItems:'center',gap:6,flexWrap:'wrap'}}>
            <span style={{fontSize:13,fontWeight:800}}>{session.branch?.name||'—'}</span>
            <span style={{fontSize:11,color:'var(--td)',background:'var(--s2)',padding:'1px 7px',borderRadius:5,fontFamily:'monospace'}}>{session.branch?.code}</span>
          </div>
          <div style={{display:'flex',gap:10,marginTop:3,flexWrap:'wrap',alignItems:'center'}}>
            <span style={{fontSize:11,color:'var(--td)',display:'flex',alignItems:'center',gap:4}}>
              <User size={10}/>{cashier?`${cashier.first_name} ${cashier.last_name}`:'Unknown'}
            </span>
            <span style={{fontSize:11,color:'var(--td)',display:'flex',alignItems:'center',gap:4}}>
              <Calendar size={10}/>{fmtDateOnly(session.opened_at)}
            </span>
            <span style={{fontSize:11,color:'var(--td)',display:'flex',alignItems:'center',gap:4}}>
              <Clock size={10}/>{fmtTime(session.opened_at)} {session.closed_at?`→ ${fmtTime(session.closed_at)}`:'→ now'}
              {' '}({dur(session.opened_at,session.closed_at)})
            </span>
          </div>
        </div>
        <div style={{display:'flex',gap:12,alignItems:'center',flexShrink:0,flexWrap:'wrap',minWidth:0}}>
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>SALES</div>
            <div style={{fontSize:16,fontWeight:900,color:'var(--primary)'}}>{fmt(session.total_sales)}</div>
          </div>
          <div style={{textAlign:'right'}}>
            <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>TXNS</div>
            <div style={{fontSize:16,fontWeight:800}}>{session.total_transactions}</div>
          </div>
          {session.status==='closed'&&<div style={{textAlign:'right'}}>
            <div style={{fontSize:10,color:'var(--td)',fontWeight:600}}>DIFF</div>
            <div style={{fontSize:14,fontWeight:800,color:diff<-0.01?'var(--danger)':diff>0.01?'var(--success)':'var(--td)'}}>
              {diff>0?'+':''}{fmt(diff)}
            </div>
          </div>}
        </div>
        <div style={{display:'flex',gap:5,alignItems:'center',flexWrap:'wrap',flexShrink:0}}>
          {methods.slice(0,3).map(m=><MethodPill key={m.name} name={m.name} amount={m.total} count={0}/>)}
          {methods.length>3&&<span style={{fontSize:10,color:'var(--td)'}}>+{methods.length-3} more</span>}
        </div>
        <div style={{display:'flex',gap:6,flexShrink:0}}>
          <button className="btn btn-primary btn-sm" onClick={()=>onView(session)}><Receipt size={13}/> Detail</button>
          <button className="btn btn-ghost btn-sm" onClick={onToggle} style={{padding:'5px 8px'}}>
            {expanded?<ChevronUp size={14}/>:<ChevronDown size={14}/>}
          </button>
        </div>
      </div>

      {expanded&&<div style={{borderTop:'1px solid var(--border)',background:'var(--s2)',padding:'14px 16px'}}>
        <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:14}}>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:8}}>PAYMENT SPLIT</div>
            {methods.length===0?<div style={{fontSize:12,color:'var(--td)'}}>No payments</div>:
              <div style={{display:'flex',flexDirection:'column',gap:5}}>
                {methods.map(m=>{
                  const pct=parseFloat(session.total_sales)>0?(m.total/parseFloat(session.total_sales)*100).toFixed(1):0;
                  const c=getMethodColor(m.name);
                  return(
                    <div key={m.name} style={{display:'flex',alignItems:'center',gap:8}}>
                      <span style={{fontSize:11,fontWeight:700,minWidth:56,color:'var(--text)'}}>{m.name}</span>
                      <div style={{flex:1,height:6,background:'var(--border)',borderRadius:3,overflow:'hidden'}}>
                        <div style={{width:`${pct}%`,height:'100%',background:c,borderRadius:3}}/>
                      </div>
                      <span style={{fontSize:11,fontWeight:700,minWidth:60,textAlign:'right'}}>{fmt(m.total)}</span>
                    </div>
                  );
                })}
              </div>}
          </div>
          <div>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:8}}>SALES BY HOUR</div>
            {(session.hourly||[]).length===0?<div style={{fontSize:12,color:'var(--td)'}}>No hourly data</div>:
              <div style={{height:80}}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={session.hourly} margin={{top:0,right:0,bottom:0,left:0}}>
                    <XAxis dataKey="hour" tickFormatter={h=>`${h}h`} tick={{fontSize:9,fill:'var(--td)'}} tickLine={false} axisLine={false}/>
                    <Tooltip formatter={v=>fmt(v)} labelFormatter={h=>`${h}:00`}
                      contentStyle={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:6,fontSize:11}}/>
                    <Bar dataKey="sales" fill="var(--primary)" radius={[2,2,0,0]}/>
                  </BarChart>
                </ResponsiveContainer>
              </div>}
          </div>
        </div>
        {(session.transaction_list||[]).length>0&&(
          <div style={{marginTop:12}}>
            <div style={{fontSize:11,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:6}}>RECENT TRANSACTIONS</div>
            <div style={{display:'flex',flexDirection:'column',gap:4}}>
              {(session.transaction_list||[]).slice(0,5).map((t,i)=>(
                <div key={i} style={{display:'flex',alignItems:'center',gap:10,fontSize:11,padding:'4px 0',borderBottom:'1px solid var(--border)'}}>
                  <span style={{fontFamily:'monospace',color:'var(--primary)',fontWeight:700,minWidth:80}}>{t.transaction_number}</span>
                  <span style={{color:'var(--td)',minWidth:50}}>{fmtTime(t.created_at)}</span>
                  <span style={{flex:1,color:'var(--tm)',overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{t.customer?.name||'Walk-in'}</span>
                  <div style={{display:'flex',gap:4}}>
                    {(t.payment_methods||[]).map((m,j)=>(
                      <span key={j} style={{fontSize:9,fontWeight:700,color:getMethodColor(m),background:getMethodColor(m)+'20',padding:'1px 5px',borderRadius:8}}>{m}</span>
                    ))}
                  </div>
                  <span style={{fontWeight:800,color:'var(--text)',minWidth:60,textAlign:'right'}}>{fmt(t.total_amount)}</span>
                </div>
              ))}
              {(session.transaction_list||[]).length>5&&(
                <div style={{fontSize:11,color:'var(--td)',textAlign:'center',paddingTop:4}}>
                  +{session.transaction_list.length-5} more — click Detail to view all
                </div>
              )}
            </div>
          </div>
        )}
      </div>}
    </div>
  );
}

/* ═══════════════════════════════════════════
   SESSION DETAIL REPORT TAB (by Order & Customer)
═══════════════════════════════════════════ */
function SessionDetailReportTab({branches}){
  const[dateFrom,setDateFrom]=useState(monthStart());
  const[dateTo,setDateTo]=useState(today());
  const[branchFilter,setBranchFilter]=useState('');
  const[loading,setLoading]=useState(false);
  const[data,setData]=useState(null);
  const[viewMode,setViewMode]=useState('orders'); // orders | customers
  const[search,setSearch]=useState('');
  const[expandedOrder,setExpandedOrder]=useState(null);
  const[expandedCustomer,setExpandedCustomer]=useState(null);
  const[sortBy,setSortBy]=useState('date_desc');
  const{t}=useLang();
  const{isOwner,myBranchId,myBranch}=useAuth();
  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const p={date_from:dateFrom,date_to:dateTo};
      if(branchFilter)p.branch_id=branchFilter;
      const r=await posAPI.getSessionsDetailReport(p);
      setData(r.data);
    }catch(e){toast.error(e.response?.data?.error||'Failed to load report')}
    finally{setLoading(false)}
  },[dateFrom,dateTo,branchFilter]);

  useEffect(()=>{load()},[load]);

  const shortcuts=[
    {label:t('posSessions.today'),fn:()=>{setDateFrom(today());setDateTo(today())}},
    {label:t('posSessions.thisWeek'),fn:()=>{const d=new Date();d.setDate(d.getDate()-d.getDay());setDateFrom(d.toISOString().split('T')[0]);setDateTo(today())}},
    {label:t('posSessions.thisMonth'),fn:()=>{setDateFrom(monthStart());setDateTo(today())}},
    {label:t('posSessions.last30'),fn:()=>{const d=new Date();d.setDate(d.getDate()-29);setDateFrom(d.toISOString().split('T')[0]);setDateTo(today())}},
  ];

  /* ── sorted + filtered orders ── */
  const filteredOrders=useMemo(()=>{
    if(!data?.orders)return[];
    let list=[...data.orders];
    if(search){
      const q=search.toLowerCase();
      list=list.filter(o=>
        (o.transaction_number||'').toLowerCase().includes(q)||
        (o.customer?.name||'').toLowerCase().includes(q)||
        (o.cashier?.name||'').toLowerCase().includes(q)||
        (o.branch?.name||'').toLowerCase().includes(q)||
        (o.items||[]).some(i=>(i.name||'').toLowerCase().includes(q))
      );
    }
    switch(sortBy){
      case'date_desc': list.sort((a,b)=>new Date(b.created_at)-new Date(a.created_at)); break;
      case'date_asc':  list.sort((a,b)=>new Date(a.created_at)-new Date(b.created_at)); break;
      case'total_desc':list.sort((a,b)=>b.total_amount-a.total_amount); break;
      case'total_asc': list.sort((a,b)=>a.total_amount-b.total_amount); break;
      default: break;
    }
    return list;
  },[data,search,sortBy]);

  const filteredCustomers=useMemo(()=>{
    if(!data?.customers)return[];
    if(!search)return data.customers;
    const q=search.toLowerCase();
    return data.customers.filter(c=>(c.name||'').toLowerCase().includes(q)||(c.phone||'').includes(q));
  },[data,search]);

  /* ── CSV Export ── */
  const exportCSV=()=>{
    if(!data)return;
    if(viewMode==='orders'){
      const rows=[['Transaction #','Date','Branch','Cashier','Customer','Customer Phone','Items','Total','Discount','Payment Methods'].join(',')];
      filteredOrders.forEach(o=>{
        rows.push([
          o.transaction_number,
          new Date(o.created_at).toLocaleString(),
          `"${o.branch?.name||'—'}"`,
          `"${o.cashier?.name||'—'}"`,
          `"${o.customer?.name||'Walk-in'}"`,
          o.customer?.phone||'—',
          o.items.length,
          o.total_amount.toFixed(2),
          o.discount_amount.toFixed(2),
          `"${o.payment_methods.map(p=>p.name).join(', ')}"`,
        ].join(','));
      });
      const blob=new Blob([rows.join('\n')],{type:'text/csv'});
      const a=document.createElement('a');a.href=URL.createObjectURL(blob);
      a.download=`pos-orders-detail-${dateFrom}-to-${dateTo}.csv`;a.click();
    }else{
      const rows=[['Customer','Phone','Orders','Total Spent','Total Discount','Last Visit'].join(',')];
      filteredCustomers.forEach(c=>{
        rows.push([`"${c.name}"`,c.phone||'—',c.order_count,c.total_spent.toFixed(2),c.total_discount.toFixed(2),c.last_visit?new Date(c.last_visit).toLocaleDateString():'—'].join(','));
      });
      const blob=new Blob([rows.join('\n')],{type:'text/csv'});
      const a=document.createElement('a');a.href=URL.createObjectURL(blob);
      a.download=`pos-customers-detail-${dateFrom}-to-${dateTo}.csv`;a.click();
    }
    toast.success('Report exported');
  };

  const s=data?.summary;

  return(
    <div>
      {/* ── Filter bar ── */}
      <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'14px 16px',marginBottom:16}}>
        <div style={{display:'flex',gap:10,flexWrap:'wrap',alignItems:'flex-end'}}>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:5}}>FROM</div>
            <input type="date" value={dateFrom} onChange={e=>setDateFrom(e.target.value)}
              style={{padding:'8px 10px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:13}}/>
          </div>
          <div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:5}}>TO</div>
            <input type="date" value={dateTo} onChange={e=>setDateTo(e.target.value)}
              style={{padding:'8px 10px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:13}}/>
          </div>
          {isOwner&&<div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:5}}>BRANCH</div>
            <select value={branchFilter} onChange={e=>setBranchFilter(e.target.value)}
              style={{padding:'8px 10px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:13,minWidth:160}}>
              <option value="">All Branches</option>
              {branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          </div>}
          {!isOwner&&myBranchId&&<div>
            <div style={{fontSize:10,fontWeight:700,color:'var(--td)',letterSpacing:.4,marginBottom:5}}>BRANCH</div>
            <div style={{display:'flex',alignItems:'center',gap:6,padding:'8px 12px',borderRadius:8,border:'1.5px solid var(--primary)',background:'var(--pm)',color:'var(--primary)',fontSize:13,fontWeight:700,height:38,boxSizing:'border-box'}}>
              🔒 {branches.find(b=>Number(b.id)===Number(myBranchId))?.name||'Your Branch'}
            </div>
          </div>}
          <button className="btn btn-primary" onClick={load} disabled={loading} style={{gap:6,height:38,alignSelf:'flex-end'}}>
            {loading?<RefreshCw size={13} style={{animation:'spin 1s linear infinite'}}/>:<Filter size={13}/>}
            {loading?t('common.loading'):t('posSessions.runReport')}
          </button>
          <button className="btn btn-ghost btn-sm" onClick={exportCSV} disabled={!data}
            style={{height:38,gap:5,alignSelf:'flex-end'}}>
            <Download size={13}/> {t('common.export')}
          </button>
        </div>
        <div style={{display:'flex',gap:5,marginTop:10,flexWrap:'wrap'}}>
          {shortcuts.map(sh=>(
            <button key={sh.label} onClick={sh.fn} className="btn btn-ghost btn-sm" style={{fontSize:11,padding:'3px 10px'}}>{sh.label}</button>
          ))}
        </div>
      </div>

      {/* ── Summary KPIs ── */}
      {s&&(
        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(155px,1fr))',gap:10,marginBottom:18}}>
          <Stat icon={<ShoppingCart size={15}/>} label="Total Orders" value={s.total_orders} color="var(--primary)"/>
          <Stat icon={<DollarSign size={15}/>} label={t('posSessions.byRevenue')} value={fmt(s.total_revenue)} color="var(--success)"/>
          <Stat icon={<TrendingDown size={15}/>} label={t('posSessions.totalDiscount')} value={fmt(s.total_discount)} color="var(--warning)"/>
          <Stat icon={<Users size={15}/>} label={t('posSessions.uniqueCustomers')} value={s.unique_customers} color="var(--info)"/>
          <Stat icon={<User size={15}/>} label="Walk-in Orders" value={s.walk_in_count} color="var(--td)"/>
          <Stat icon={<TrendingUp size={15}/>} label="Avg Order" value={fmt(s.avg_order)} color="var(--accent)"/>
          {s.usd_collected>0&&<Stat icon={<span style={{fontWeight:900,fontSize:13}}>$</span>} label="Collected USD" value={fmt(s.usd_collected)} color="var(--success)"/>}
          {s.khr_collected>0&&<Stat icon={<span style={{fontWeight:900,fontSize:12}}>៛</span>} label="Collected KHR" value={`៛${Math.round(s.khr_collected).toLocaleString()}`} color="var(--accent)"/>}
        </div>
      )}

      {loading&&<div style={{textAlign:'center',padding:50,color:'var(--td)'}}><RefreshCw size={20} style={{animation:'spin 1s linear infinite'}}/><div style={{marginTop:10,fontSize:13}}>Loading report…</div></div>}

      {!loading&&data&&(
        <>
          {/* ── View mode toggle + search + sort ── */}
          <div style={{display:'flex',gap:10,alignItems:'center',marginBottom:14,flexWrap:'wrap'}}>
            {/* Toggle */}
            <div style={{display:'flex',background:'var(--s2)',borderRadius:9,padding:3,gap:2}}>
              {[['orders','By Order','ShoppingCart'],['customers','By Customer','Users']].map(([mode,label])=>(
                <button key={mode} onClick={()=>{setViewMode(mode);setSearch('');setExpandedOrder(null);setExpandedCustomer(null)}}
                  style={{padding:'6px 14px',borderRadius:7,border:'none',cursor:'pointer',fontSize:12,fontWeight:700,
                    background:viewMode===mode?'var(--primary)':'transparent',
                    color:viewMode===mode?'#fff':'var(--td)',transition:'all .15s'}}>
                  {label}
                </button>
              ))}
            </div>
            {/* Search */}
            <div style={{position:'relative',flex:'1 1 200px',minWidth:160}}>
              <Search size={13} style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:'var(--td)'}}/>
              <input className="form-input" value={search} onChange={e=>setSearch(e.target.value)}
                placeholder={viewMode==='orders'?'Search order #, customer, product…':'Search customer, phone…'}
                style={{paddingLeft:28,fontSize:12}}/>
            </div>
            {/* Sort (orders only) */}
            {viewMode==='orders'&&(
              <select value={sortBy} onChange={e=>setSortBy(e.target.value)}
                style={{padding:'7px 10px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12}}>
                <option value="date_desc">Newest First</option>
                <option value="date_asc">Oldest First</option>
                <option value="total_desc">Highest Total</option>
                <option value="total_asc">Lowest Total</option>
              </select>
            )}
            <span style={{fontSize:12,color:'var(--td)',flexShrink:0}}>
              {viewMode==='orders'?`${filteredOrders.length} orders`:`${filteredCustomers.length} customers`}
            </span>
          </div>

          {/* ══ ORDERS VIEW ══ */}
          {viewMode==='orders'&&(
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {filteredOrders.length===0&&(
                <div style={{textAlign:'center',padding:60,color:'var(--td)'}}>
                  <ShoppingCart size={40} color="var(--border)" style={{display:'block',margin:'0 auto 12px'}}/>
                  No orders found
                </div>
              )}
              {filteredOrders.map(o=>{
                const isOpen=expandedOrder===o.id;
                return(
                  <div key={o.id} style={{background:'var(--surface)',border:`1.5px solid ${isOpen?'var(--primary)':'var(--border)'}`,
                    borderRadius:12,overflow:'hidden',transition:'border-color .15s'}}>
                    {/* Order header row */}
                    <div onClick={()=>setExpandedOrder(isOpen?null:o.id)}
                      style={{display:'flex',alignItems:'center',gap:12,padding:'12px 14px',cursor:'pointer',flexWrap:'wrap'}}>
                      {/* Tx # */}
                      <div style={{minWidth:100}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase',letterSpacing:.3}}>Order #</div>
                        <div style={{fontSize:13,fontWeight:800,color:'var(--primary)',fontFamily:'monospace'}}>{o.transaction_number}</div>
                      </div>
                      {/* Date */}
                      <div style={{minWidth:110}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase',letterSpacing:.3}}>Date</div>
                        <div style={{fontSize:12,fontWeight:600}}>{fmtDateTime(o.created_at)}</div>
                      </div>
                      {/* Customer */}
                      <div style={{flex:'1 1 130px',minWidth:100}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase',letterSpacing:.3}}>Customer</div>
                        <div style={{fontSize:12,fontWeight:700,display:'flex',alignItems:'center',gap:5}}>
                          <User size={11} color={o.customer?'var(--primary)':'var(--td)'}/>
                          {o.customer?.name||<span style={{color:'var(--td)',fontStyle:'italic'}}>Walk-in</span>}
                        </div>
                        {o.customer?.phone&&<div style={{fontSize:10,color:'var(--td)'}}>{o.customer.phone}</div>}
                      </div>
                      {/* Branch */}
                      <div style={{minWidth:90}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase',letterSpacing:.3}}>Branch</div>
                        <div style={{fontSize:12,fontWeight:600}}>{o.branch?.name||'—'}</div>
                      </div>
                      {/* Cashier */}
                      <div style={{minWidth:90}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase',letterSpacing:.3}}>Cashier</div>
                        <div style={{fontSize:12,fontWeight:600}}>{o.cashier?.name||'—'}</div>
                      </div>
                      {/* Payment */}
                      <div style={{display:'flex',gap:4,flexWrap:'wrap',flex:'1 1 100px'}}>
                        {o.payment_methods.map((p,i)=>(
                          <MethodPill key={i} name={p.name} amount={p.amount} count={0}
                            originalAmount={p.original_amount} originalCurrency={p.original_currency}/>
                        ))}
                      </div>
                      {/* Total */}
                      <div style={{textAlign:'right',minWidth:80}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase',letterSpacing:.3}}>Total</div>
                        <div style={{fontSize:16,fontWeight:900,color:'var(--success)'}}>{fmt(o.total_amount)}</div>
                        {o.total_amount_khr>0&&<div style={{fontSize:10,color:'var(--td)'}}>≈ ៛{Math.round(o.total_amount_khr).toLocaleString()}</div>}
                        {o.discount_amount>0&&<div style={{fontSize:10,color:'var(--warning)'}}>-{fmt(o.discount_amount)} disc</div>}
                      </div>
                      {/* Expand icon */}
                      <div style={{color:'var(--td)',flexShrink:0}}>
                        {isOpen?<ChevronUp size={16}/>:<ChevronDown size={16}/>}
                      </div>
                    </div>

                    {/* Expanded: item list */}
                    {isOpen&&(
                      <div style={{borderTop:'1px solid var(--border)',padding:'12px 14px',background:'var(--s2)'}}>
                        <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:8}}>Order Items</div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))',gap:6}}>
                          {o.items.map((item,idx)=>(
                            <div key={idx} style={{display:'flex',justifyContent:'space-between',alignItems:'center',
                              padding:'8px 10px',background:'var(--surface)',borderRadius:8,border:'1px solid var(--border)'}}>
                              <div>
                                <div style={{fontSize:12,fontWeight:700}}>{item.name}</div>
                                <div style={{fontSize:10,color:'var(--td)'}}>{item.sku} · qty {item.quantity} × {fmt(item.unit_price)}</div>
                                {item.discount>0&&<div style={{fontSize:10,color:'var(--warning)'}}>disc -{fmt(item.discount)}</div>}
                              </div>
                              <div style={{fontWeight:800,fontSize:13,color:'var(--text)'}}>{fmt(item.line_total)}</div>
                            </div>
                          ))}
                        </div>
                        {/* Totals summary */}
                        <div style={{display:'flex',gap:16,marginTop:12,paddingTop:10,borderTop:'1px dashed var(--border)',justifyContent:'flex-end',flexWrap:'wrap'}}>
                          <div style={{fontSize:12,color:'var(--td)'}}>Subtotal: <strong>{fmt(o.subtotal)}</strong></div>
                          {o.discount_amount>0&&<div style={{fontSize:12,color:'var(--warning)'}}>Discount: <strong>-{fmt(o.discount_amount)}</strong></div>}
                          {o.tax_amount>0&&<div style={{fontSize:12,color:'var(--td)'}}>Tax: <strong>{fmt(o.tax_amount)}</strong></div>}
                          <div style={{fontSize:14,fontWeight:900,color:'var(--success)'}}>Total: {fmt(o.total_amount)}</div>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* ══ CUSTOMERS VIEW ══ */}
          {viewMode==='customers'&&(
            <div style={{display:'flex',flexDirection:'column',gap:8}}>
              {filteredCustomers.length===0&&(
                <div style={{textAlign:'center',padding:60,color:'var(--td)'}}>
                  <Users size={40} color="var(--border)" style={{display:'block',margin:'0 auto 12px'}}/>
                  No customer data found
                </div>
              )}
              {filteredCustomers.map((c,rank)=>{
                const isOpen=expandedCustomer===c.id;
                return(
                  <div key={c.id} style={{background:'var(--surface)',border:`1.5px solid ${isOpen?'var(--primary)':'var(--border)'}`,
                    borderRadius:12,overflow:'hidden',transition:'border-color .15s'}}>
                    {/* Customer header */}
                    <div onClick={()=>setExpandedCustomer(isOpen?null:c.id)}
                      style={{display:'flex',alignItems:'center',gap:14,padding:'12px 16px',cursor:'pointer',flexWrap:'wrap'}}>
                      {/* Rank badge */}
                      <div style={{width:36,height:36,borderRadius:'50%',flexShrink:0,
                        background:rank===0?'#f59e0b22':rank===1?'#94a3b822':rank===2?'#cd7c2222':'var(--s2)',
                        color:rank===0?'#f59e0b':rank===1?'#94a3b8':rank===2?'#cd7c22':'var(--td)',
                        display:'flex',alignItems:'center',justifyContent:'center',fontSize:14,fontWeight:900}}>
                        {rank===0?'🥇':rank===1?'🥈':rank===2?'🥉':rank+1}
                      </div>
                      {/* Name + phone */}
                      <div style={{flex:'1 1 160px',minWidth:120}}>
                        <div style={{fontSize:14,fontWeight:800,display:'flex',alignItems:'center',gap:6}}>
                          <User size={13} color={c.id==='walk-in'?'var(--td)':'var(--primary)'}/>
                          {c.name}
                        </div>
                        {c.phone&&c.phone!=='—'&&<div style={{fontSize:11,color:'var(--td)'}}>{c.phone}</div>}
                      </div>
                      {/* Stats */}
                      <div style={{display:'grid',gridTemplateColumns:'repeat(4,auto)',gap:'0 20px',flexShrink:0,flexWrap:'wrap'}}>
                        <div style={{textAlign:'center'}}>
                          <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase'}}>Orders</div>
                          <div style={{fontSize:16,fontWeight:900}}>{c.order_count}</div>
                        </div>
                        <div style={{textAlign:'center'}}>
                          <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase'}}>Total Spent</div>
                          <div style={{fontSize:16,fontWeight:900,color:'var(--success)'}}>{fmt(c.total_spent)}</div>
                        </div>
                        <div style={{textAlign:'center'}}>
                          <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase'}}>Avg Order</div>
                          <div style={{fontSize:14,fontWeight:700}}>{fmt(c.order_count?c.total_spent/c.order_count:0)}</div>
                        </div>
                        <div style={{textAlign:'center'}}>
                          <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase'}}>Discount</div>
                          <div style={{fontSize:14,fontWeight:700,color:'var(--warning)'}}>{c.total_discount>0?'-'+fmt(c.total_discount):'—'}</div>
                        </div>
                      </div>
                      {/* Last visit */}
                      <div style={{minWidth:100,textAlign:'right',flexShrink:0}}>
                        <div style={{fontSize:10,color:'var(--td)',fontWeight:600,textTransform:'uppercase'}}>Last Visit</div>
                        <div style={{fontSize:11,fontWeight:600}}>{c.last_visit?fmtDateOnly(c.last_visit):'—'}</div>
                      </div>
                      <div style={{color:'var(--td)',flexShrink:0}}>
                        {isOpen?<ChevronUp size={16}/>:<ChevronDown size={16}/>}
                      </div>
                    </div>

                    {/* Expanded: order list */}
                    {isOpen&&(
                      <div style={{borderTop:'1px solid var(--border)',padding:'12px 16px',background:'var(--s2)'}}>
                        <div style={{fontSize:11,fontWeight:700,color:'var(--td)',textTransform:'uppercase',letterSpacing:.4,marginBottom:8}}>
                          All Orders ({c.orders.length})
                        </div>
                        <div style={{display:'flex',flexDirection:'column',gap:5}}>
                          {c.orders.map(o=>(
                            <div key={o.id} style={{display:'flex',alignItems:'center',justifyContent:'space-between',
                              padding:'8px 12px',background:'var(--surface)',borderRadius:8,border:'1px solid var(--border)'}}>
                              <div style={{display:'flex',alignItems:'center',gap:12}}>
                                <span style={{fontFamily:'monospace',fontSize:12,fontWeight:700,color:'var(--primary)'}}>{o.number}</span>
                                <span style={{fontSize:11,color:'var(--td)'}}>{fmtDateTime(o.date)}</span>
                                {o.branch&&<span style={{fontSize:11,color:'var(--td)',background:'var(--s2)',padding:'2px 7px',borderRadius:10}}>{o.branch}</span>}
                              </div>
                              <span style={{fontWeight:800,fontSize:13,color:'var(--success)'}}>{fmt(o.total)}</span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════ */
export default function PosSessionsPage(){
   const{t}=useLang();
  const{hasPermission,isOwner,myBranchId,myBranch}=useAuth();
  const can={viewSessions:hasPermission('pos.sessions_view')||hasPermission('pos.access'),viewReports:hasPermission('pos.sessions_report')||hasPermission('pos.access'),posAccess:hasPermission('pos.access'),canOpen:hasPermission('pos.open_session'),canClose:hasPermission('pos.close_session')||hasPermission('pos.close_register')};
  const[sessions,setSessions]=useState([]);
  const[branches,setBranches]=useState([]);
  const[loading,setLoading]=useState(true);
  const[detail,setDetail]=useState(null);
  const[expandedId,setExpandedId]=useState(null);
  const[selectedBranch,setSelectedBranch]=useState(()=>myBranchId&&!isOwner?myBranchId:null);
  const[status,setStatus]=useState('');
  const[search,setSearch]=useState('');
  const[sessDateFrom,setSessDateFrom]=useState('');
  const[sessDateTo,setSessDateTo]=useState('');
  const[page,setPage]=useState(1);
  const[totalPages,setTotalPages]=useState(1);
  const[activeTab,setActiveTab]=useState('sessions');

  const load=useCallback(async()=>{
    setLoading(true);
    try{
      const params={limit:30,page,status:status||undefined};
      if(!isOwner&&myBranchId)params.branch_id=myBranchId;
      else if(selectedBranch)params.branch_id=selectedBranch;
      if(sessDateFrom)params.date_from=sessDateFrom;
      if(sessDateTo)params.date_to=sessDateTo;
      const r=await posAPI.getSessions(params);
      setSessions(r.data.data||[]);
      setTotalPages(r.data.pages||1);
    }catch{toast.error('Failed to load sessions')}
    finally{setLoading(false)}
  },[page,status,selectedBranch,sessDateFrom,sessDateTo]);

  useEffect(()=>{load()},[load]);
  useEffect(()=>{branchAPI.getAll({limit:100}).then(r=>setBranches(r.data.data||[])).catch(()=>{})},[]);

  const filtered=sessions.filter(s=>{
    if(!search)return true;
    const q=search.toLowerCase();
    return(s.branch?.name||'').toLowerCase().includes(q)||
      (s.cashier?.first_name||'').toLowerCase().includes(q)||
      (s.cashier?.last_name||'').toLowerCase().includes(q)||
      String(s.id).includes(q);
  });

  const totalSales=filtered.reduce((s,x)=>s+parseFloat(x.total_sales||0),0);
  const totalTxns=filtered.reduce((s,x)=>s+(x.total_transactions||0),0);
  const openCount=filtered.filter(s=>s.status==='open').length;
  const closedCount=filtered.filter(s=>s.status==='closed').length;

  const tabBtn=(t,icon,label)=>(
    <button onClick={()=>setActiveTab(t)}
      style={{display:'flex',alignItems:'center',gap:6,padding:'8px 16px',fontSize:13,fontWeight:600,
        border:'none',borderBottom:`2px solid ${activeTab===t?'var(--primary)':'transparent'}`,
        background:'transparent',cursor:'pointer',color:activeTab===t?'var(--primary)':'var(--tm)',
        marginBottom:-1,transition:'color .15s'}}>
      {icon}{label}
    </button>
  );

  if(!can.viewSessions&&!can.viewReports)return<NoPerm action="view POS sessions or reports"/>;
  return(
    <div>
      <PageHeader title={t('posSessions.title')}
        subtitle={`${sessions.length} session${sessions.length!==1?'s':''} · ${openCount} open`}
        action={<button className="btn btn-ghost btn-sm" onClick={()=>{setPage(1);load()}} style={{display:'flex',alignItems:'center',gap:5}}>
          <RefreshCw size={13}/> Refresh
        </button>}/>

      {/* Top Stats */}
      <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(160px,1fr))',gap:10,marginBottom:16}}>
        <Stat icon={<DollarSign size={15}/>} label={t('posSessions.totalSales')} value={fmt(totalSales)} color="var(--primary)"/>
        <Stat icon={<ShoppingCart size={15}/>} label={t('common.transactions')} value={totalTxns} color="var(--success)"/>
        <Stat icon={<Monitor size={15}/>} label={t('posSessions.openSessions')} value={openCount} color="var(--success)"/>
        <Stat icon={<CheckCircle size={15}/>} label={t('posSessions.closedSessions')} value={closedCount} color="var(--td)"/>
        <Stat icon={<TrendingUp size={15}/>} label={t('posSessions.avgPerSession')}
          value={fmt(closedCount?totalSales/closedCount:0)} color="var(--info)"/>
      </div>

      {/* Tab bar */}
      
      <div style={{display:'flex',gap:0,marginBottom:16,borderBottom:'1px solid var(--border)'}}>
        {can.viewSessions&&tabBtn('sessions',<Monitor size={13}/>, t('posSessions.sessionList'))}
        {can.viewReports&&tabBtn('reports',<BarChart2 size={13}/>, t('posSessions.branchReports'))}
        {can.viewReports&&tabBtn('detail',<Receipt size={13}/>, 'Order & Customer Detail')}
      </div>

      {/* SESSION LIST TAB */}
      {activeTab==='sessions'&&<>
        {/* Filters */}
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:12,padding:'12px 14px',marginBottom:14,
          display:'flex',gap:8,flexWrap:'wrap',alignItems:'center'}}>
          <div style={{position:'relative',flex:'1 1 180px',minWidth:140}}>
            <Search size={13} style={{position:'absolute',left:9,top:'50%',transform:'translateY(-50%)',color:'var(--td)'}}/>
            <input className="form-input" value={search} onChange={e=>setSearch(e.target.value)}
              placeholder="Search branch, cashier..." style={{paddingLeft:28,fontSize:12}}/>
          </div>
          <select className="form-input" value={status} onChange={e=>{setStatus(e.target.value);setPage(1)}}
            style={{flex:'0 0 auto',fontSize:12,padding:'7px 10px'}}>
            <option value="">All Statuses</option>
            <option value="open">Open</option>
            <option value="closed">Closed</option>
            <option value="closing">Closing</option>
          </select>
          {isOwner?(
          <select className="form-input" value={selectedBranch||''} onChange={e=>{setSelectedBranch(e.target.value?parseInt(e.target.value):null);setPage(1)}}
            style={{flex:'0 0 auto',fontSize:12,padding:'7px 10px'}}>
            <option value="">All Branches</option>
            {branches.map(b=><option key={b.id} value={b.id}>{b.name}</option>)}
          </select>
          ):(
          <div style={{display:'flex',alignItems:'center',gap:5,padding:'7px 10px',borderRadius:8,background:'var(--pm)',border:'1.5px solid var(--primary)',fontSize:12,fontWeight:600,color:'var(--primary)',flexShrink:0}}>
            🔒 {myBranch?.name||'Your Branch'}
          </div>
          )}
          <input type="date" value={sessDateFrom} onChange={e=>{setSessDateFrom(e.target.value);setPage(1)}}
            title="From date"
            style={{padding:'7px 9px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12}}/>
          <input type="date" value={sessDateTo} onChange={e=>{setSessDateTo(e.target.value);setPage(1)}}
            title="To date"
            style={{padding:'7px 9px',borderRadius:8,border:'1.5px solid var(--border)',background:'var(--s2)',color:'var(--text)',fontSize:12}}/>
          {(status||selectedBranch||search||sessDateFrom||sessDateTo)&&(
            <button className="btn btn-ghost btn-sm" onClick={()=>{setStatus('');setSelectedBranch(null);setSearch('');setSessDateFrom('');setSessDateTo('');setPage(1)}}>
              <X size={12}/> Clear
            </button>
          )}
        </div>

        {loading?<div style={{textAlign:'center',padding:40,color:'var(--td)'}}><RefreshCw size={18} style={{animation:'spin 1s linear infinite'}}/> Loading...</div>:
        filtered.length===0?<div style={{textAlign:'center',padding:60,color:'var(--td)'}}>
          <Monitor size={40} color="var(--border)" style={{marginBottom:12,display:'block',margin:'0 auto 12px'}}/>
          No sessions found
        </div>:
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {filtered.map(s=>(
            <SessionCard key={s.id} session={s}
              expanded={expandedId===s.id}
              onToggle={()=>setExpandedId(expandedId===s.id?null:s.id)}
              onView={setDetail}/>
          ))}
        </div>}

        {totalPages>1&&<div style={{display:'flex',justifyContent:'center',alignItems:'center',gap:8,marginTop:16}}>
          <button className="btn btn-ghost btn-sm" disabled={page<=1} onClick={()=>setPage(p=>p-1)}>← Prev</button>
          <span style={{fontSize:12,color:'var(--td)'}}>Page {page} of {totalPages}</span>
          <button className="btn btn-ghost btn-sm" disabled={page>=totalPages} onClick={()=>setPage(p=>p+1)}>Next →</button>
        </div>}
      </>}

      {/* BRANCH REPORTS TAB */}
      {activeTab==='reports'&&<BranchReportTab branches={branches}/>}

      {/* ORDER & CUSTOMER DETAIL TAB */}
      {activeTab==='detail'&&<SessionDetailReportTab branches={branches}/>}

      {detail&&<SessionDetailModal session={detail} onClose={()=>setDetail(null)}/>}
    </div>
  );
}
